/** @jsxImportSource react */
import { cn } from '@common/lib/clsxm';
import FieldAdapter from '@react/form/components/FieldAdapter';
import Logo from '@react/shared/components/Logo';
import { Button } from '@react/system';
import { ValidChecklists } from '@react/tools/constants/data';
import {
  alChecklistData,
  ilChecklistData,
  mcChecklistData,
} from '@react/tools/constants/tour-checklist';
import { Image, Note } from '@sly/icons/react';
import html2canvas from 'html2canvas';
import React, { type JSX } from 'react';
import { Field, Form } from 'react-final-form';

const BoxSection = ({
  icon,
  title,
  children,
  className,
}: {
  icon: JSX.Element;
  title: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <section
    className={cn(
      'break-inside-avoid rounded-xl border border-slate-lighter-95',
      className
    )}
  >
    <div className='flex items-center gap-4 border-b py-4 px-4 md:px-6'>
      {icon}
      <h3 className='font-medium print:text-black'>{title}</h3>
    </div>
    {children}
  </section>
);

const ChecklistSection = ({
  title,
  items,
  icon,
}: {
  title: string;
  items: string[];
  icon: JSX.Element;
}) => (
  <BoxSection icon={icon} title={title}>
    <ul className='space-y-2 py-4 px-4 [&_button]:p-2'>
      {items.map((item, index) => (
        <li key={index} className='flex items-center gap-4'>
          <Field
            name={`${title}-${index}`}
            type='checkbox'
            render={({ input }) => (
              <input
                {...input}
                id={`${title}-${index}`}
                className='form-checkbox h-5 w-5 print:border-black'
              />
            )}
          />
          <label htmlFor={`${title}-${index}`} className='print:text-black'>
            {item}
          </label>
        </li>
      ))}
    </ul>
  </BoxSection>
);

function getChecklistData(type: ValidChecklists) {
  switch (type) {
    case 'assisted-living-touring-checklist':
      return alChecklistData;

    case 'independent-living-touring-checklist':
      return ilChecklistData;
    case 'memory-care-touring-checklist':
      return mcChecklistData;

    default:
      return null;
  }
}

function getPDFVersion(type: ValidChecklists) {
  switch (type) {
    case 'assisted-living-touring-checklist':
      return 'https://seniorly.s3.us-west-1.amazonaws.com/Touring+checklist_+AL.pdf';
    case 'independent-living-touring-checklist':
      return 'https://seniorly.s3.us-west-1.amazonaws.com/Touring+checklist_+IL.pdf';
    case 'memory-care-touring-checklist':
      return 'https://seniorly.s3.us-west-1.amazonaws.com/Touring+checklist_+MC.pdf';
    default:
      null;
  }
}

interface AssistedLivingChecklistProps {
  heading: string;
  type: ValidChecklists;
}

const AssistedLivingChecklist = ({
  heading,
  type,
}: AssistedLivingChecklistProps) => {
  const checklistRef = React.createRef<HTMLFormElement>();
  const actionsRef = React.createRef<HTMLDivElement>();
  const headerRef = React.createRef<HTMLDivElement>();

  const checklistItems = getChecklistData(type);

  if (!checklistItems) {
    return null;
  }

  const pdfLink = getPDFVersion(type);

  const handlePrint = () => {
    if (!pdfLink) return;
    const link = document.createElement('a');
    link.href = pdfLink;
    link.download = "Seniorly's Guide to Senior Living.pdf";
    link.target = '_blank'; // Open in a new tab
    link.click();
  };

  const handleSaveAsImage = async () => {
    if (actionsRef.current) {
      actionsRef.current.style.display = 'none';
    }

    if (headerRef.current) {
      headerRef.current.style.display = 'flex';
    }

    if (checklistRef.current) {
      checklistRef.current.style.paddingLeft = '1rem';
      checklistRef.current.style.paddingRight = '1rem';
      try {
        const canvas = await html2canvas(checklistRef.current);
        const image = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = image;
        link.download = 'touring-checklist.png';
        link.click();
      } catch (error) {
        console.error('Error saving checklist as image', error);
      }
      checklistRef.current.style.paddingLeft = '0';
      checklistRef.current.style.paddingRight = '0';
    }
    if (actionsRef.current) {
      actionsRef.current.style.display = 'flex';
    }

    if (headerRef.current) {
      headerRef.current.style.display = 'none';
    }
  };

  return (
    <Form
      onSubmit={handleSaveAsImage}
      render={({ handleSubmit }) => {
        return (
          <form
            onSubmit={handleSubmit}
            ref={checklistRef}
            className='w-full space-y-8 rounded-lg bg-white py-6 print:p-0 print:shadow-none'
          >
            <div
              ref={headerRef}
              className='hidden items-center justify-between gap-4'
            >
              <div className='font-t-l'>{heading}</div>
              <Logo />
            </div>
            <div className='grid items-start gap-6 md:grid-cols-2'>
              <Field
                name='communityName'
                label='Community name'
                component={FieldAdapter}
                type='searchAutoComplete'
                createable={true}
                placeholder=''
              />
              <Field
                label='Date toured'
                name='dateToured'
                component={FieldAdapter}
                type='date'
              />
              {Object.entries(checklistItems).map(([section, items]) => (
                <ChecklistSection
                  key={section}
                  title={section}
                  items={items.items}
                  icon={items.icon}
                />
              ))}
              <BoxSection icon={<Note />} title='Notes'>
                <div className='py-4 px-4'>
                  <Field
                    name='notes'
                    component={FieldAdapter}
                    type='textarea'
                    rows={15}
                    hideBorder={true}
                  />
                </div>
              </BoxSection>
            </div>
            <div
              ref={actionsRef}
              className='flex flex-col items-end justify-end gap-2 print:hidden md:flex-row md:items-start'
            >
              {pdfLink && (
                <Button
                  type='button'
                  onClick={handlePrint}
                  variant='outlined'
                  eventProps={{
                    text: 'Download PDF',
                    location: `${heading} tool`,
                  }}
                >
                  <div className='flex items-center gap-2'>
                    <Note />
                    Download PDF
                  </div>
                </Button>
              )}

              <Button
                type='button'
                onClick={handleSaveAsImage}
                variant='outlined'
                eventProps={{
                  text: 'Download image',
                  location: `${heading} tool`,
                }}
              >
                <div className='flex items-center gap-2'>
                  {/* eslint-disable-next-line jsx-a11y/alt-text */}
                  <Image />
                  Download image
                </div>
              </Button>
            </div>
          </form>
        );
      }}
    />
  );
};

export default AssistedLivingChecklist;
